import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { Icon } from '../components/Icon';
import { TabBar } from '../editor/TabBar';
import { TagInput } from '../editor/TagInput';
import {
  type TabId,
  type EditorForm,
  emptyForm,
  visibleTabs,
  allComplete,
  incompleteTabs,
  TAB_LABELS,
  STRATEGIC_VALUE_OPTIONS,
} from '../editor/completion';
import { portalApi, ApiError } from '../api/client';
import type { IdeaCreateRequest, TierResponse, FieldValidationError, PortalDomain } from '../api/types';
import { useBusinessCase, type UseBusinessCaseResult } from '../components/business-case/useBusinessCase';
import { KpiStrip } from '../components/business-case/KpiStrip';
import { PlChart } from '../components/business-case/PlChart';
import { CostAccordion } from '../components/business-case/CostAccordion';
import { SavingsAccordion } from '../components/business-case/SavingsAccordion';

const STANDARD_ONLY: TierResponse = { business_case: false, improvements: false, maturity: false };

/**
 * 5-tab idea editor (`/ideas/new`, `/ideas/:id/edit`) — US4.
 *
 * Fetches the tenant tier on load BEFORE rendering gated tabs (AC4.4) so a user
 * never sees fields outside their tier. All visible tabs are freely navigable
 * (AC4.2). Submit POSTs (new) or PUTs (edit); 422 field errors render inline
 * and their owning tab reflects the incomplete state (AC4.7).
 */
export function EditorPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [tier, setTier] = useState<TierResponse | null>(null);
  const [form, setForm] = useState<EditorForm>(emptyForm());
  const [tools, setTools] = useState<string[]>([]);
  const [domains, setDomains] = useState<PortalDomain[]>([]);
  const [active, setActive] = useState<TabId>('details');
  const [savedId, setSavedId] = useState<string | null>(id ?? null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isDraft, setIsDraft] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savedBanner, setSavedBanner] = useState(false);
  const savedBannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [tierRes, toolsRes, domainsRes] = await Promise.all([
          portalApi.getTier(),
          portalApi.listTools(),
          portalApi.listDomains(),
        ]);
        if (cancelled) return;
        setTier(tierRes);
        setTools(toolsRes.tools.map((t) => t.name));
        setDomains(domainsRes.domains);
        if (isEdit && id) {
          const idea = await portalApi.getIdea(id);
          if (cancelled) return;
          setIsDraft(idea.is_draft ?? true);
          const isBizCase = tierRes.business_case;
          setForm((f) => ({
            ...f,
            name: idea.name,
            description: idea.description ?? '',
            domainId: idea.domain_id ?? null,
            // For BUSINESS_CASE tenants the Value tab is gone; do NOT load
            // valueType/importance/justification/monetaryEstimate from the idea
            // (leave at emptyForm() defaults) so stale strategic data on a
            // pre-existing biz-case draft is neutralised (US7).
            valueType: isBizCase
              ? null
              : idea.is_monetary
                ? 'monetary'
                : idea.strategic_value != null
                  ? 'strategic'
                  : null,
            importance: isBizCase ? 0 : snapStrategicValue(idea.strategic_value),
            justification: isBizCase ? '' : (idea.strategic_value_justification ?? ''),
            monetaryEstimate: isBizCase
              ? ''
              : idea.monetary_value != null
                ? String(idea.monetary_value)
                : '',
            tools: idea.tools ?? [],
          }));
        }
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 401)) {
          // Tier fetch failed (non-auth) — fall back to Standard-only so the
          // editor still renders the base tabs rather than blanking out.
          if (!cancelled) setTier(STANDARD_ONLY);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  // Business-case overlay state. The hook only fetches for BUSINESS_CASE
  // tenants with a saved idea (line items FK to product.id); otherwise it stays
  // idle (ideaId=null → no fetch).
  const bizCaseIdeaId = tier?.business_case ? savedId : null;
  const businessCase = useBusinessCase(bizCaseIdeaId);

  // Thread line-item presence into the form so the completion layer (US7) gates
  // the bizcase tab on hasCost && hasSavings rather than the retired scalars.
  useEffect(() => {
    setForm((f) =>
      f.hasCost === businessCase.hasCost && f.hasSavings === businessCase.hasSavings
        ? f
        : { ...f, hasCost: businessCase.hasCost, hasSavings: businessCase.hasSavings },
    );
  }, [businessCase.hasCost, businessCase.hasSavings]);

  const tabs = useMemo(() => (tier ? visibleTabs(tier) : []), [tier]);
  const complete = tier ? allComplete(form, tier) : false;
  const incompletes = tier ? incompleteTabs(form, tier) : [];
  const isReadOnly = isEdit && !isDraft;

  function update<K extends keyof EditorForm>(key: K, value: EditorForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setSubmitError(null);
  }

  function buildPayload(): IdeaCreateRequest {
    const payload: IdeaCreateRequest = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      tools: form.tools.length ? form.tools : null,
    };
    // For BUSINESS_CASE tenants the Value tab is hidden and the business case is
    // expressed via cost/savings LINE ITEMS (written through the portal
    // cost/savings API, not the idea payload). So omit the value fields
    // (monetary_value / strategic_value / justification) AND the retired scalar
    // cost / savings keys entirely; the server forces is_monetary=true.
    if (!tier?.business_case) {
      if (form.valueType === 'monetary') {
        payload.monetary_value = form.monetaryEstimate ? Number(form.monetaryEstimate) : null;
      } else if (form.valueType === 'strategic') {
        payload.strategic_value = form.importance || null;
        payload.strategic_value_justification = form.justification.trim() || null;
      }
    }
    if (tier?.improvements) {
      payload.improvements = form.improvements.trim() || null;
    }
    // Domain is methodology-free metadata, not a value field — always sent
    // (including for BUSINESS_CASE tenants). An empty selection clears it.
    payload.domain_id = form.domainId ?? null;
    return payload;
  }

  function showSavedBanner() {
    setSavedBanner(true);
    if (savedBannerTimer.current) clearTimeout(savedBannerTimer.current);
    savedBannerTimer.current = setTimeout(() => setSavedBanner(false), 10000);
  }

  async function handleSave() {
    setSaving(true);
    setFieldErrors({});
    setSubmitError(null);
    try {
      const payload = buildPayload();
      const result = await portalApi.saveDraft(savedId, payload);
      if (!savedId) setSavedId(result.id);
      showSavedBanner();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const detail = err.detail as FieldValidationError | undefined;
        if (detail && typeof detail === 'object' && 'field' in detail) {
          setFieldErrors({ [detail.field]: detail.message });
        } else {
          setSubmitError(err.message);
        }
      } else if (!(err instanceof ApiError && err.status === 401)) {
        setSubmitError('Could not save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit() {
    setSubmitting(true);
    setFieldErrors({});
    setSubmitError(null);
    try {
      const payload = buildPayload();
      const result = await portalApi.submitIdea(savedId, payload);
      if (!savedId) setSavedId(result.id);
      navigate('/ideas');
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const detail = err.detail as FieldValidationError | undefined;
        if (detail && typeof detail === 'object' && 'field' in detail) {
          setFieldErrors({ [detail.field]: detail.message });
          const owner = tabForField(detail.field);
          if (owner && tabs.includes(owner)) setActive(owner);
        } else {
          setSubmitError(err.message);
        }
      } else if (!(err instanceof ApiError && err.status === 401)) {
        setSubmitError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading || !tier) {
    return (
      <div className="min-h-screen bg-bg">
        <Hero crumb={<span>Loading…</span>} />
        <div className="mx-auto max-w-[720px] px-9 py-6 text-sm text-muted">Loading editor…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg">
      <Hero
        crumb={
          <span className="flex items-center gap-1.5">
            <button className="text-white/75 hover:underline" onClick={() => navigate('/ideas')}>
              My ideas
            </button>
            <Icon name="chevronRSm" size={9} />
            <span className="font-semibold">{form.name || (isEdit ? 'Edit idea' : 'New idea')}</span>
          </span>
        }
      />
      <div className="mx-auto max-w-[940px] px-9 pb-28 pt-6">
        <div className="flex items-start gap-5">
          {/* ── Editor card ── */}
          <div className="min-w-0 flex-1 overflow-hidden rounded-card border border-line bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <TabBar tabs={tabs} active={active} form={form} tier={tier} onSelect={setActive} />

            {savedBanner && (
              <div className="flex items-center gap-2 border-b border-success-mid bg-success-light px-5 py-2.5 text-sm font-semibold text-success">
                <Icon name="checkG" size={14} /> Draft saved.
              </div>
            )}

            <div className="p-6">
              {active === 'details' && (
                <DetailsTab form={form} update={update} errors={fieldErrors} readOnly={isReadOnly} domains={domains} />
              )}
              {active === 'tools' && (
                <ToolsTab tags={form.tools} suggestions={tools} onChange={(t) => update('tools', t)} error={fieldErrors.tools} readOnly={isReadOnly} />
              )}
              {active === 'value' && !tier.business_case && (
                <ValueTab form={form} update={update} errors={fieldErrors} readOnly={isReadOnly} />
              )}
              {active === 'bizcase' && tier.business_case && (
                <BizcaseTab
                  savedId={savedId}
                  readOnly={isReadOnly}
                  businessCase={businessCase}
                />
              )}
              {active === 'improvements' && tier.improvements && (
                <ImprovementsTab form={form} update={update} errors={fieldErrors} readOnly={isReadOnly} />
              )}
            </div>

            <div className="flex items-center justify-between border-t border-line bg-panel px-6 py-3.5">
              {isReadOnly ? (
                <>
                  <div className="text-xs text-muted">This idea has been submitted and cannot be edited.</div>
                  <button
                    onClick={() => navigate('/ideas')}
                    className="rounded-[9px] border-[1.5px] border-line bg-white px-3.5 py-1.5 text-[0.79rem] font-semibold text-ink hover:border-accent hover:text-accent"
                  >
                    Back to ideas
                  </button>
                </>
              ) : (
                <>
                  <div className="text-xs text-muted">
                    {submitError ? (
                      <span className="text-danger">{submitError}</span>
                    ) : (
                      'Click any section to edit it. No set order required.'
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate('/ideas')}
                      className="rounded-[9px] border-[1.5px] border-line bg-white px-3.5 py-1.5 text-[0.79rem] font-semibold text-ink hover:border-accent hover:text-accent"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving || submitting}
                      className="inline-flex items-center gap-1.5 rounded-[9px] border-[1.5px] border-line bg-white px-3.5 py-1.5 text-[0.79rem] font-semibold text-ink hover:border-accent hover:text-accent disabled:opacity-60"
                    >
                      <Icon name="saveSm" size={13} /> {saving ? 'Saving…' : 'Save draft'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Submit panel ── */}
          <div className="w-[168px] shrink-0 sticky top-6">
            <div className="rounded-card border border-line bg-white p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              {isReadOnly ? (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-success">
                  <Icon name="checkG" size={12} /> Submitted
                </div>
              ) : complete ? (
                <>
                  <div className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-success">
                    <Icon name="checkG" size={12} /> All sections done
                  </div>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || saving}
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-[9px] bg-accent px-3 py-2 text-[0.79rem] font-semibold text-white hover:bg-accent-hover disabled:opacity-60"
                  >
                    {submitting ? 'Submitting…' : 'Submit idea'}
                    {!submitting && <Icon name="chevronR" size={11} />}
                  </button>
                </>
              ) : (
                <>
                  <div className="mb-2 text-[0.68rem] font-bold uppercase tracking-wide text-muted">
                    Still needed
                  </div>
                  <ul className="mb-3 space-y-1.5">
                    {incompletes.map((tab) => (
                      <li key={tab}>
                        <button
                          onClick={() => setActive(tab)}
                          className="text-left text-xs text-muted hover:text-accent hover:underline"
                        >
                          · {TAB_LABELS[tab]}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button
                    disabled
                    className="w-full cursor-not-allowed rounded-[9px] bg-line px-3 py-2 text-[0.79rem] font-semibold text-muted"
                  >
                    Submit idea
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Snaps a stored strategic_value to the nearest of the five Vane Loop levels
 * {1, 3, 5, 7, 9}, so legacy portal drafts (stored 1–4) load onto a valid
 * button. Unset (null) stays 0 — the portal treats it as "not answered".
 */
function snapStrategicValue(v: number | null): number {
  if (v == null) return 0;
  return v <= 2 ? 1 : v <= 4 ? 3 : v <= 6 ? 5 : v <= 8 ? 7 : 9;
}

/** Maps an API error field name to the editor tab that owns it. */
function tabForField(field: string): TabId | null {
  if (field === 'name' || field === 'description') return 'details';
  if (field === 'tools') return 'tools';
  if (field === 'value' || field === 'strategic_value' || field === 'monetary_value') return 'value';
  if (field === 'cost' || field === 'savings') return 'bizcase';
  if (field === 'improvements' || field === 'driver_improvements') return 'improvements';
  return null;
}

// ── Tab bodies ──

type UpdateFn = <K extends keyof EditorForm>(key: K, value: EditorForm[K]) => void;

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div className="mt-1 flex items-center gap-1 text-xs text-danger">
      <Icon name="warnXs" size={11} /> {message}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted">
      {children} <span className="text-danger">*</span>
    </label>
  );
}

const inputCls =
  'w-full rounded-[9px] border-[1.5px] border-line bg-white px-3 py-2.5 text-sm text-ink outline-none focus:border-accent';

function DetailsTab({ form, update, errors, readOnly, domains }: { form: EditorForm; update: UpdateFn; errors: Record<string, string>; readOnly?: boolean; domains: PortalDomain[] }) {
  const roCls = readOnly ? ' bg-panel cursor-default' : '';
  return (
    <div>
      <div className="mb-0.5 text-base font-bold">Idea details</div>
      <div className="mb-5 text-sm text-muted">Give your idea a clear name and explain what it does.</div>
      <div className="mb-4">
        <Label>Idea name</Label>
        <input className={`${inputCls}${roCls}`} readOnly={readOnly} value={form.name} onChange={(e) => update('name', e.target.value)} />
        <FieldError message={errors.name} />
      </div>
      <div className={domains.length > 0 ? 'mb-4' : ''}>
        <Label>Description</Label>
        <textarea
          className={`${inputCls} min-h-[82px] resize-y${roCls}`}
          readOnly={readOnly}
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
        />
        {!readOnly && (
          <div className="mt-1 text-xs text-muted">
            What is this use case? What problem does it solve and who benefits?
          </div>
        )}
        <FieldError message={errors.description} />
      </div>
      {domains.length > 0 && (
        <div>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted">
            Domain {!readOnly && '(optional)'}
          </label>
          <select
            className={`${inputCls}${roCls}`}
            disabled={readOnly}
            value={form.domainId ?? ''}
            onChange={(e) => update('domainId', e.target.value === '' ? null : e.target.value)}
          >
            <option value="">No domain</option>
            {domains.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <FieldError message={errors.domain_id} />
        </div>
      )}
    </div>
  );
}

function ToolsTab({
  tags,
  suggestions,
  onChange,
  error,
  readOnly,
}: {
  tags: string[];
  suggestions: string[];
  onChange: (t: string[]) => void;
  error?: string;
  readOnly?: boolean;
}) {
  return (
    <div>
      <div className="mb-0.5 text-base font-bold">Tools &amp; systems used</div>
      <div className="mb-5 text-sm text-muted">
        Which software, databases or systems would this idea involve? Add as many as apply.
      </div>
      <Label>Tools</Label>
      <TagInput tags={tags} onChange={onChange} suggestions={suggestions} readOnly={readOnly} />
      <FieldError message={error} />
    </div>
  );
}

function ValueTab({ form, update, errors, readOnly }: { form: EditorForm; update: UpdateFn; errors: Record<string, string>; readOnly?: boolean }) {
  const roCls = readOnly ? ' bg-panel cursor-default' : '';
  return (
    <div>
      <div className="mb-0.5 text-base font-bold">Value type</div>
      <div className="mb-5 text-sm text-muted">
        How does this idea create value? Select the primary type and how important it is
        strategically.
      </div>
      <div className="mb-4">
        <Label>Primary value</Label>
        <div className="grid grid-cols-2 gap-2.5">
          <ValueOption
            active={form.valueType === 'strategic'}
            icon="chartSm"
            title="Strategic value"
            desc="Improves capability, quality or competitive position"
            onClick={() => update('valueType', 'strategic')}
            disabled={readOnly}
          />
          <ValueOption
            active={form.valueType === 'monetary'}
            icon="moneySm"
            title="Monetary value"
            desc="Saves costs or generates additional revenue"
            onClick={() => update('valueType', 'monetary')}
            disabled={readOnly}
          />
        </div>
        <FieldError message={errors.value || errors.monetary_value} />
      </div>
      {form.valueType === 'monetary' ? (
        <div>
          <Label>Estimated annual value (€)</Label>
          <input
            type="number"
            min="0"
            className={`${inputCls}${roCls}`}
            placeholder="0"
            readOnly={readOnly}
            value={form.monetaryEstimate}
            onChange={(e) => update('monetaryEstimate', e.target.value)}
          />
          {!readOnly && <div className="mt-1 text-xs text-muted">Expected savings or revenue uplift per year</div>}
          <FieldError message={errors.monetary_value} />
        </div>
      ) : (
        <div>
          <Label>Strategic importance</Label>
          <div className="flex gap-1.5">
            {STRATEGIC_VALUE_OPTIONS.map(({ label, value }) => {
              const on = form.importance === value;
              return (
                <button
                  key={value}
                  disabled={readOnly}
                  onClick={() => update('importance', value)}
                  className={`flex-1 rounded-lg border-[1.5px] px-1 py-2.5 text-[0.79rem] font-semibold transition-all disabled:cursor-default ${
                    on ? 'border-accent bg-accent-light text-accent' : 'border-line bg-white text-muted hover:border-accent hover:text-accent disabled:hover:border-line disabled:hover:text-muted'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <FieldError message={errors.strategic_value} />
          {form.valueType === 'strategic' && (
            <div className="mt-4">
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted">
                Why this rating? {!readOnly && '(optional)'}
              </label>
              <textarea
                className={`${inputCls} min-h-[82px] resize-y${roCls}`}
                readOnly={readOnly}
                placeholder={readOnly ? '' : 'Briefly explain your strategic value rating so reviewers understand your reasoning.'}
                value={form.justification}
                onChange={(e) => update('justification', e.target.value)}
              />
              <FieldError message={errors.strategic_value_justification} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ValueOption({
  active,
  icon,
  title,
  desc,
  onClick,
  disabled,
}: {
  active: boolean;
  icon: 'chartSm' | 'moneySm';
  title: string;
  desc: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded-[10px] border-[1.5px] p-4 text-left transition-all disabled:cursor-default ${
        active ? 'border-accent bg-accent-light' : 'border-line hover:border-accent disabled:hover:border-line'
      }`}
    >
      <div className={`mb-1 flex items-center gap-2 text-sm font-bold ${active ? 'text-accent' : ''}`}>
        <Icon name={icon} size={14} /> {title}
      </div>
      <div className={`pl-[21px] text-[0.74rem] leading-snug ${active ? 'text-[#3B72B8]' : 'text-muted'}`}>
        {desc}
      </div>
    </button>
  );
}

/**
 * Business case tab — the full cost/savings overlay (US6). Replaces the old
 * two scalar inputs with a KPI strip + P&L chart + cost/savings accordions
 * backed by the portal cost/savings API.
 *
 * Line items FK to the product, so the overlay is only usable once the idea is
 * saved (`savedId !== null`); before that it prompts to save first. When the
 * idea is submitted (`readOnly`), the overlay renders read-only (display only,
 * no add/edit/delete affordances) — consistent with the US1 draft-gate.
 */
function BizcaseTab({
  savedId,
  readOnly,
  businessCase,
}: {
  savedId: string | null;
  readOnly: boolean;
  businessCase: UseBusinessCaseResult;
}) {
  const { loading, error, data, formatCurrency } = businessCase;

  if (!savedId) {
    return (
      <div>
        <div className="mb-0.5 text-base font-bold">Business case</div>
        <div className="mb-5 text-sm text-muted">
          Build the business case from real cost and savings line items.
        </div>
        <div className="rounded-[10px] border border-dashed border-line bg-panel px-4 py-8 text-center text-sm text-muted">
          Save the idea first to add costs and savings.
        </div>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div>
        <div className="mb-0.5 text-base font-bold">Business case</div>
        <div className="py-8 text-center text-sm text-muted">
          {error ?? 'Loading business case…'}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-0.5 text-base font-bold">Business case</div>
      <div className="mb-4 text-sm text-muted">
        {readOnly
          ? 'This idea has been submitted; the business case is read-only.'
          : 'Add cost and savings line items to build the business case.'}
      </div>

      <div className="mb-4">
        <KpiStrip costs={data.costs} savings={data.savings} formatCurrency={formatCurrency} />
      </div>
      <div className="mb-5">
        <PlChart costs={data.costs} savings={data.savings} />
      </div>

      <div className="mb-6">
        <CostAccordion
          costs={data.costs}
          drivers={data.drivers}
          formatCurrency={formatCurrency}
          readOnly={readOnly}
          onCreate={businessCase.createCost}
          onUpdate={businessCase.updateCost}
          onDelete={businessCase.deleteCost}
        />
      </div>
      <div>
        <SavingsAccordion
          savings={data.savings}
          categories={data.categories}
          formatCurrency={formatCurrency}
          readOnly={readOnly}
          onCreate={businessCase.createSavings}
          onUpdate={businessCase.updateSavings}
          onDelete={businessCase.deleteSavings}
        />
      </div>
    </div>
  );
}

function ImprovementsTab({ form, update, errors, readOnly }: { form: EditorForm; update: UpdateFn; errors: Record<string, string>; readOnly?: boolean }) {
  return (
    <div>
      <div className="mb-0.5 text-base font-bold">Process improvements</div>
      <div className="mb-5 text-sm text-muted">
        Describe what process, outcome or experience this use case would improve. Be specific
        about who benefits and how.
      </div>
      <Label>What does this improve?</Label>
      <textarea
        className={`${inputCls} min-h-[110px] resize-y${readOnly ? ' bg-panel cursor-default' : ''}`}
        readOnly={readOnly}
        placeholder={readOnly ? '' : 'e.g. Reduces the time finance staff spend manually reviewing expense reports…'}
        value={form.improvements}
        onChange={(e) => update('improvements', e.target.value)}
      />
      <FieldError message={errors.improvements} />
    </div>
  );
}
